// hooks/useCachedImage.js
import { useEffect, useMemo, useState } from "react";

const memCache = new Map(); // in-memory to avoid thrashing FileReader

const fetchAsDataURL = async (url) => {
  const res = await fetch(url, { mode: "cors", cache: "force-cache" });
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  const blob = await res.blob();
  await new Promise((r) => setTimeout(r, 0)); // yield
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export function useCachedImage(url, { keyPrefix = "imgcache", ttlDays = 7 } = {}) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);
  const storageKey = useMemo(() => `${keyPrefix}:${url}`, [keyPrefix, url]);

  useEffect(() => {
    if (!url) return;

    // 1) in-memory cache first
    if (memCache.has(url)) {
      setSrc(memCache.get(url));
      return;
    }

    // 2) localStorage with TTL
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const { dataUrl, ts } = JSON.parse(raw);
        const isExpired = Date.now() - ts > ttlDays * 24 * 60 * 60 * 1000;
        if (!isExpired && dataUrl) {
          memCache.set(url, dataUrl);
          setSrc(dataUrl);
          return;
        }
      }
    } catch (_) {}

    // 3) fetch -> dataURL -> store
    let cancelled = false;
    (async () => {
      try {
        const dataUrl = await fetchAsDataURL(url);
        if (cancelled) return;
        memCache.set(url, dataUrl);
        setSrc(dataUrl);
        try {
          localStorage.setItem(storageKey, JSON.stringify({ dataUrl, ts: Date.now() }));
        } catch (e) {
          // quota or private mode — just keep in memory
          // console.warn("localStorage not available/over quota", e);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e);
        setSrc(url); // graceful fallback to network URL
      }
    })();

    return () => { cancelled = true; };
  }, [url, storageKey, ttlDays]);

  return { src, error };
}
